// ─── "הבוקר בקצרה" / "היום בקצרה" · the sentences (sprint 19, stage 2) ─────
//
// Built in code from fixed templates · no model writes a word here. Every
// figure comes from the arithmetic already on the screen: the day's move and
// each holding's contribution (portfolioMovers.js), the S&P 500 of the same
// session, and the extended-hours estimate (extendedHours.js).
//
// A sentence is a list of segments: a string, or a figure { pct, signed }
// that the card draws isolated LTR, with the real minus, or { symbol }. `briefText` joins
// them, for the tests and for a screen reader.
import { fmtPct, shownDir } from "./formatters";

// How much of the portfolio's move the top contributor accounts for · the
// fixed table. Below a quarter, or more than the whole move (the rest of the
// portfolio moved the other way), no sentence: "כמעט כל" would understate
// it and the table has no word for "more than all".
// The verb agrees with the rise or the fall itself, as Hebrew does with a
// quantity of a noun ("רוב העלייה הגיעה", "כרבע מהעלייה הגיעה").
export const SHARE_TABLE = [
  { min: 0.9,  phrase: noun => `כמעט כל ה${noun} הגיעה` },
  { min: 0.6,  phrase: noun => `רוב ה${noun} הגיעה` },
  { min: 0.4,  phrase: noun => `כמחצית מה${noun} הגיעה` },
  { min: 0.25, phrase: noun => `כרבע מה${noun} הגיעה` },
];

export function sharePhrase(ratio, noun) {
  if (!Number.isFinite(ratio) || ratio > 1) return null;
  const row = SHARE_TABLE.find(r => ratio >= r.min);
  return row ? row.phrase(noun) : null;
}

// `tone`: the direction the figure is coloured by · its own sign when
// signed, the portfolio's when the verb carries the sign.
const pctSeg = (pct, signed = false, tone = shownDir(pct)) => ({ pct, signed, tone });

/**
 * The first sentence and its follow-up.
 *   total   · { pct } the portfolio's move in the session
 *   leader  · { symbol, points } the largest contribution (percentage points)
 *   open    · whether the session is still trading
 *   today   · whether the session is today's (after the close: "היום")
 *   weekday · "שני", the session's day (otherwise)
 *   index   · { pct } the S&P 500 in the same session, or null
 * → [segments] · empty when there is no move to describe
 */
// A move as words and an unsigned figure: "עלה 1.42%", "ירד 0.40%", "לא
// השתנה" · the verb says the direction, so the figure carries no sign.
function moved(pct) {
  const d = shownDir(pct);
  return d === 0 ? ["לא השתנה"] : [d > 0 ? "עלה " : "ירד ", pctSeg(Math.abs(pct), false, d)];
}

export function mainSentence({ total, leader, open, today = false, weekday, index }) {
  if (!total || !Number.isFinite(total.pct)) return [];
  const when = open ? "מאז הפתיחה היום, התיק" : today ? "היום התיק" : `ביום ${weekday} התיק`;
  const dir = shownDir(total.pct);
  // The S&P 500 of the same session, as a second clause with its own verb ·
  // left out when there is no quote from that session.
  const sp = index && Number.isFinite(index.pct) ? [", וה-", { latin: "S&P 500" }, " ", ...moved(index.pct)] : [];
  const out = [`${when} `, ...moved(total.pct), ...sp, "."];
  if (dir === 0) return out;

  // The second sentence: only when the leader moved the same way as the
  // portfolio, and its share is in the table.
  if (leader && Number.isFinite(leader.points) && Math.sign(leader.points) === dir) {
    const phrase = sharePhrase(leader.points / total.pct, dir > 0 ? "עלייה" : "ירידה");
    if (phrase) out.push(` ${phrase} מ-`, { symbol: leader.symbol }, ".");
  }
  return out;
}

/**
 * The extended-hours sentence, or [] when there is nothing fresh to say.
 *   state    · "pre" | "post" (marketStatus)
 *   estimate · estimateExtended() for the portfolio, or null
 *   future   · futureFor(ES=F), or null · before the open only
 */
export function extendedSentence({ state, estimate, future }) {
  const est = estimate && Number.isFinite(estimate.pct) ? estimate : null;
  if (state === "pre") {
    const fut = future && Number.isFinite(future.pct) ? future : null;
    if (fut && est) return ["לפני הפתיחה: חוזי ", { latin: "S&P 500" }, " ", pctSeg(fut.pct, true), ", והתיק במסחר המוקדם ", pctSeg(est.pct, true), " (משוער)."];
    if (fut) return ["לפני הפתיחה: חוזי ", { latin: "S&P 500" }, " ", pctSeg(fut.pct, true), "."];
    if (est) return ["לפני הפתיחה: התיק במסחר המוקדם ", pctSeg(est.pct, true), " (משוער)."];
    return [];
  }
  if (state === "post" && est) return ["אחרי הסגירה: התיק ", pctSeg(est.pct, true), " (משוער)."];
  return [];
}

// "הבוקר בקצרה" from 05:00 to noon on the reader's clock, "היום בקצרה" the
// rest of the day · after midnight is not "the morning" yet.
export function briefTitle(now = new Date()) {
  const h = now.getHours();
  return h >= 5 && h < 12 ? "הבוקר בקצרה" : "היום בקצרה";
}

// The segments as plain text.
export function briefText(segs) {
  return (segs || []).map(s => (typeof s === "string" ? s
    : s.symbol ? s.symbol
    : s.latin ? s.latin
    : fmtPct(s.pct, { signed: s.signed }))).join("");
}
